import { Css, LoadingSkeleton } from "@homebound/beam";
import { FormattedDate } from "src/components/FormattedDate";
import { formatToPrice } from "src/components/Price";
import { ImpactCard } from "src/routes/projects/change-events/guided/components/ImpactCard";
import { pluralize, signedCount } from "src/utils";
import { Temporal } from "temporal-polyfill";
import { ReviewImpactedPrice } from "../../components/ReviewImpactedPrice";
import { ReviewProjectImpact_ScheduleImpactFragment } from "../ReviewImpactStep.generated";
import { ReviewImpactBudgetTotals, scheduleShift } from "../reviewImpactUtils";

type ProjectImpactCardsProps = {
  totals: ReviewImpactBudgetTotals;
  /** How many project items the project has today, i.e. before the configuration is applied. */
  currentItemCount: number;
  /** How many project items the project would have after it. */
  newItemCount: number;
  scheduleImpact: ReviewProjectImpact_ScheduleImpactFragment | undefined;
};

/** The "Budget / Scope / Schedule Impact" cards at the top of the Review Impact step. */
export function ProjectImpactCards(props: ProjectImpactCardsProps) {
  const { totals, currentItemCount, newItemCount, scheduleImpact } = props;
  const { currentBudgetInCents, newBudgetInCents, priceImpactInCents } = totals;
  const budgetDifferenceInCents = newBudgetInCents - currentBudgetInCents;
  const itemDifference = newItemCount - currentItemCount;

  return (
    <div css={Css.dg.gtc("repeat(3, 1fr)").gap3.$}>
      <ImpactCard
        icon="invoiceDollar"
        title="Budget Impact"
        headline={<ReviewImpactedPrice valueInCents={budgetDifferenceInCents} />}
        rows={[
          { label: "Current Budget", value: formatToPrice({ valueInCents: currentBudgetInCents }) },
          { label: "New Budget", value: formatToPrice({ valueInCents: newBudgetInCents }) },
          { label: "Budget Difference", value: signedPrice(budgetDifferenceInCents) },
          { label: "Price Difference", value: signedPrice(priceImpactInCents) },
        ]}
      />
      <ImpactCard
        icon="list"
        title="Scope Impact"
        headline={signedCount(itemDifference, "item")}
        rows={[
          {
            label: "Current Scope",
            value: currentItemCount === 0 ? "None" : `${currentItemCount} ${pluralize(currentItemCount, "item")}`,
          },
          {
            label: "New Scope",
            value: newItemCount === 0 ? "None" : `${newItemCount} ${pluralize(newItemCount, "item")}`,
          },
          {
            label: "Difference",
            value: currentItemCount === newItemCount ? "None" : `${itemDifference > 0 ? "+" : ""}${itemDifference}`,
          },
        ]}
      />
      <ScheduleImpactCard scheduleImpact={scheduleImpact} />
    </div>
  );
}

type ScheduleImpactCardProps = { scheduleImpact: ReviewProjectImpact_ScheduleImpactFragment | undefined };

const scheduleImpactLabels = ["Current Completion", "Projected Completion", "Difference"];

function ScheduleImpactCard(props: ScheduleImpactCardProps) {
  const { scheduleImpact } = props;
  // The projection runs a schedule simulation, so it lands well after the budget and scope numbers do
  if (!scheduleImpact) {
    return (
      <ImpactCard
        icon="calendar"
        title="Schedule Impact"
        headline={<LoadingSkeleton rows={1} columns={1} size="sm" />}
        rows={scheduleImpactLabels.map((label) => ({
          label,
          value: <LoadingSkeleton rows={1} columns={1} size="sm" />,
        }))}
      />
    );
  }

  const { currentCompletionDate, proposedCompletionDate, differenceInDays } = scheduleImpact;
  return (
    <ImpactCard
      icon="calendar"
      title="Schedule Impact"
      headline={<ScheduleImpactHeadline scheduleImpact={scheduleImpact} />}
      rows={[
        { label: "Current Completion", value: completionDate(currentCompletionDate) },
        { label: "Projected Completion", value: completionDate(proposedCompletionDate) },
        { label: "Difference", value: differenceInDays ? signedCount(differenceInDays, "day") : "None" },
      ]}
    />
  );
}

/** A plain, uncolored delta, i.e. "+$800,000.00"; only the card's headline is colored. */
function signedPrice(valueInCents: number) {
  return formatToPrice({ valueInCents, displayDirection: valueInCents !== 0 });
}

function completionDate(date: Temporal.PlainDate | null | undefined) {
  return date ? <FormattedDate date={date} /> : "None";
}

type ScheduleImpactHeadlineProps = { scheduleImpact: ReviewProjectImpact_ScheduleImpactFragment };

function ScheduleImpactHeadline(props: ScheduleImpactHeadlineProps) {
  const { scheduleImpact } = props;
  const shift = scheduleShift(scheduleImpact);
  if (!shift) return "None";
  return <span css={Css.if(shift.count > 0).red600.else.green600.$}>{signedCount(shift.count, shift.unit)}</span>;
}
